import { useEffect, useState } from 'react';
import api, { formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import PhoneInput from '@/components/PhoneInput';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MapPin, Plus, Pencil, Trash2, Star } from 'lucide-react';

const TIMEZONES = [
  'America/Mexico_City', 'America/Monterrey', 'America/Cancun', 'America/Tijuana',
  'America/Bogota', 'America/Lima', 'America/Santiago', 'America/Buenos_Aires',
  'America/New_York', 'America/Los_Angeles', 'Europe/Madrid', 'UTC',
];

export default function Locations() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editLoc, setEditLoc] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', timezone: 'America/Mexico_City', phone: '', email: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetch = () => {
    api.get('/tenant/locations').then(r => { setLocations(r.data); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { fetch(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.post('/tenant/locations', form);
      setShowCreate(false);
      setForm({ name: '', address: '', timezone: 'America/Mexico_City', phone: '', email: '' });
      fetch();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editLoc) return;
    setSaving(true);
    setError('');
    try {
      await api.put(`/tenant/locations/${editLoc.id}`, form);
      setEditLoc(null);
      fetch();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta sede?')) return;
    try {
      await api.delete(`/tenant/locations/${id}`);
      fetch();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    }
  };

  const openEdit = (loc) => {
    setForm({ name: loc.name, address: loc.address || '', timezone: loc.timezone || '', phone: loc.phone || '', email: loc.email || '' });
    setEditLoc(loc);
  };

  return (
    <div data-testid="locations-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] tracking-tight">Sedes</h1>
          <p className="text-sm text-[#64748B] mt-1">Administra las ubicaciones de tu negocio</p>
        </div>
        <Button onClick={() => { setForm({ name: '', address: '', timezone: 'America/Mexico_City', phone: '', email: '' }); setShowCreate(true); }}
          className="bg-[#F72585] text-white hover:bg-[#D81B60] w-full sm:w-auto" data-testid="add-location-btn">
          <Plus className="w-4 h-4 mr-2" /> Nueva Sede
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-[#E2E8F0] border-t-[#0F172A] rounded-full animate-spin" />
        </div>
      ) : locations.length === 0 ? (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-12 text-center">
          <MapPin className="w-12 h-12 text-[#E2E8F0] mx-auto mb-3" />
          <p className="text-sm text-[#64748B]">Sin sedes configuradas</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {locations.map(loc => (
            <div key={loc.id} className="bg-white border border-[#E2E8F0] rounded-xl p-5 hover:-translate-y-0.5 transition-all" data-testid={`location-card-${loc.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#F72585]" strokeWidth={1.5} />
                  <h3 className="text-sm font-semibold text-[#0F172A]">{loc.name}</h3>
                  {loc.is_main && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />}
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(loc)} className="p-1.5 text-[#64748B] hover:text-[#0F172A] transition-colors" data-testid={`edit-loc-${loc.id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  {!loc.is_main && (
                    <button onClick={() => handleDelete(loc.id)} className="p-1.5 text-[#64748B] hover:text-red-500 transition-colors" data-testid={`delete-loc-${loc.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
              {loc.address && <p className="text-xs text-[#64748B] mb-1">{loc.address}</p>}
              <div className="flex flex-wrap gap-2 mt-3">
                <span className="text-[10px] px-2 py-0.5 bg-[#F8F9FA] text-[#64748B] rounded-full border border-[#E2E8F0]">{loc.timezone}</span>
                {loc.phone && <span className="text-[10px] px-2 py-0.5 bg-[#F8F9FA] text-[#64748B] rounded-full border border-[#E2E8F0]">{loc.phone}</span>}
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${
                  loc.is_active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-600 border-slate-200'
                }`}>{loc.is_active ? 'Activa' : 'Inactiva'}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={showCreate || !!editLoc} onOpenChange={(o) => { if (!o) { setShowCreate(false); setEditLoc(null); } }}>
        <DialogContent className="max-w-md" data-testid="location-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#0F172A]">{editLoc ? 'Editar Sede' : 'Nueva Sede'}</DialogTitle>
          </DialogHeader>
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}
          <form onSubmit={editLoc ? handleUpdate : handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Nombre</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="bg-white border-[#E2E8F0] text-sm" data-testid="loc-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Dirección</Label>
              <Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} className="bg-white border-[#E2E8F0] text-sm" data-testid="loc-address-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Zona horaria</Label>
              <Select value={form.timezone} onValueChange={v => setForm({...form, timezone: v})}>
                <SelectTrigger className="bg-white border-[#E2E8F0] text-sm" data-testid="loc-timezone-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TIMEZONES.map(tz => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Teléfono</Label>
                <PhoneInput value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="bg-white border-[#E2E8F0] text-sm" data-testid="loc-phone-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest text-[#64748B] font-semibold">Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="bg-white border-[#E2E8F0] text-sm" data-testid="loc-email-input" />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => { setShowCreate(false); setEditLoc(null); }}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-[#F72585] text-white hover:bg-[#D81B60]" data-testid="save-location-btn">
                {saving ? 'Guardando...' : (editLoc ? 'Actualizar' : 'Crear Sede')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
